import React, { useEffect, useState } from "react";
import { Dropdown, Form, Modal, Placeholder } from "react-bootstrap";

import DstAlert from "../../../../../components/DstAlert/DstAlert";
import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../../../components/Popup/Popup";
import RequiredLabel from "../../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../../contexts/SessionContext";
import { calculateEndDate, formatToISODateTime, isPast } from "../../../../../services/TimeService";
import translate from "../../../../../services/Translate";
import {
  Animator,
  CreateMeetingModalProps,
  LanguageKeys,
  MeetingLinkService,
  MeetingTypes,
  VisibilityKeys,
} from "./CreateMeetingModal.d";
import {
  createMeeting,
  getMeetingTypes,
  getResources,
  languageMapping,
  visibilityOptions,
} from "./CreateMeetingModal.function";

const CreateMeetingModal = ({
  language,
  projectId,
  projectName,
  setShowCreationModal,
  showCreationModal,
  onSuccess,
}: CreateMeetingModalProps) => {
  const { email } = useSession();
  const [animators, setAnimators] = useState<Animator[]>([]);
  const [description, setDescription] = useState<string>("");
  const [duration, setDuration] = useState(60);
  const [durationError, setDurationError] = useState(false);
  const [meetingLinkService, setMeetingLinkService] = useState<MeetingLinkService>("zoom");
  const [meetingLink, setMeetingLink] = useState<string>("");
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypes[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedmeetingType, setSelectedMeetingType] = useState<MeetingTypes | null>(null);
  const [selectedAnimators, setSelectedAnimators] = useState<Animator[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageKeys>("fr-FR");
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<number | null>(projectId);
  const [selectedVisibility, setSelectedVisibility] = useState<VisibilityKeys>("private");
  const [startDate, setStartDate] = useState<string>("");
  const [title, setTitle] = useState<string>(`Project Meeting: ${projectName}`);
  const [loading, setLoading] = useState({
    types: true,
    resources: true,
    projects: true,
    submit: false,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const displayAlert = (props: any) => {
    setAlertProps(props);
    setShowAlert(true);
  };

  const _handleAlertClose = () => {
    setShowAlert(false);
    resetSelection();
  };

  useEffect(() => {
    getMeetingTypes(setMeetingTypes, setLoading, language);
    getResources(setAnimators, setPrograms, setLoading, language);
  }, [language]);

  useEffect(() => {
    setSelectedMeetingType(
      meetingTypes.find((meetingType) => meetingType.name.toLowerCase() === "call projet") || null
    );
  }, [meetingTypes]);

  useEffect(() => {
    if (animators.length && !selectedAnimators.length) {
      const sessionUserAnimator = animators.find((animator) => animator.email === email);
      if (sessionUserAnimator) setSelectedAnimators([sessionUserAnimator]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animators]);

  const handleConfirm = () => {
    const optionalFields = {
      animator_ids:
        selectedAnimators.length > 0 ? selectedAnimators.map((animator) => animator.staff_member_id) : undefined,
      description: description || "",
      url: meetingLink || "",
    };

    const payload = {
      dt_end: calculateEndDate(startDate, duration),
      service_link_generation: meetingLinkService,
      language: selectedLanguage,
      program_id: selectedProgram?.id,
      name: title,
      type_id: selectedmeetingType?.id,
      project_id: selectedProject,
      dt_start: new Date(startDate).toISOString(),
      visibility: selectedVisibility,
      ...optionalFields,
    };

    createMeeting(payload, language, setLoading, displayAlert, setShowCreationModal, resetSelection, onSuccess);
  };

  const resetSelection = () => {
    setDescription("");
    setDuration(60);
    setDurationError(false);
    setMeetingLinkService("zoom");
    setMeetingLink("");
    setSelectedMeetingType(
      meetingTypes.find((meetingType) => meetingType.name.toLowerCase() === "call projet") || null
    );
    const sessionUserAnimator = animators.find((animator) => animator.email === email);
    setSelectedAnimators(sessionUserAnimator ? [sessionUserAnimator] : []);
    setSelectedLanguage("fr-FR");
    setSelectedProgram(null);
    setSelectedProject(projectId);
    setSelectedVisibility("private");
    setStartDate("");
    setTitle(`Project Meeting: ${projectName}`);
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = +event.target.value;

    if (value > 0) {
      setDurationError(false);
      setDuration(value);
    } else {
      setDurationError(true);
      setDuration(0);
    }
  };

  const isFormValid = (() =>
    !!title && !!startDate && !isPast(startDate) && !!selectedLanguage && !!selectedProject && !!duration)();

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.TITLE")}
        scrollable
      >
        <Form id="modal-add-meeting">
          <Form.Group>
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.MEETING_TYPE")}
              />
            </Form.Label>
            {meetingTypes.length > 0 ? (
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                  disabled
                >
                  {selectedmeetingType?.name}
                </Dropdown.Toggle>
              </Dropdown>
            ) : (
              <Placeholder animation="glow">
                <Placeholder.Button className="w-100" />
              </Placeholder>
            )}
          </Form.Group>
          <React.Fragment>
            <Form.Group className="mt-3">
              <Form.Label>
                {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.PROGRAM")}
              </Form.Label>
              <MultiSelectDropdown
                btnWidth="100%"
                label={
                  selectedProgram?.name ||
                  translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.PROGRAM")
                }
                items={programs}
                onConfirm={(selectedItems) => setSelectedProgram(selectedItems?.[0] || null)}
                badgeItemProperty="name"
                displayItem={(item) => item.name}
                language={language}
                toggleClass={`text-truncate border-secondary w-100 ${!selectedProgram ? "text-muted" : ""}`}
                isSingleSelect
                mock={!programs.length}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel
                  label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.LANGUAGE")}
                />
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                >
                  {languageMapping(language)[selectedLanguage]}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {Object.keys(languageMapping(language)).map((key) => (
                    <Dropdown.Item key={key} onClick={() => setSelectedLanguage(key as LanguageKeys)}>
                      {languageMapping(language)[key as LanguageKeys]}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </React.Fragment>

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.PROJECT")}
              />
            </Form.Label>
            <MultiSelectDropdown
              btnWidth="100%"
              disabled
              label={projectName}
              items={[]}
              onConfirm={() => {}}
              badgeItemProperty="display"
              displayItem={() => <></>}
              language={language}
              toggleClass="text-truncate border-secondary w-100 disabled"
              isSingleSelect
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.TITLE")}
              />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!title ? "text-muted" : ""}`}
              type="text"
              placeholder={translate(
                language,
                "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.PLACEHOLDERS.TITLE_SELECT"
              )}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.START_DATE")}
              />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!startDate ? "text-muted" : ""}`}
              type="datetime-local"
              min={formatToISODateTime(new Date().toISOString(), true).replace(" ", "T")}
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.DURATION")}
              />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${durationError ? "border-danger" : ""}`}
              type="number"
              value={duration}
              min={1}
              onChange={handleDurationChange}
            />
            {durationError && (
              <Form.Text className="text-danger">
                {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.ERRORS.UNFILLED_DURATION")}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.VISIBILITY")}
            </Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex justify-content-between align-items-center border-secondary"
                variant=""
              >
                {visibilityOptions(language)[selectedVisibility]}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                <Dropdown.Item onClick={() => setSelectedVisibility("shared")}>
                  {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.VISIBILITY.SHARED")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSelectedVisibility("private")}>
                  {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.VISIBILITY.PRIVATE")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.ORGANIZER")}
            </Form.Label>
            <Form.Control className="border-secondary" type="text" readOnly value={email} disabled />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.ANIMATORS")}
            </Form.Label>
            <MultiSelectDropdown
              mock={animators.length === 0}
              btnWidth="100%"
              label={
                selectedAnimators.length > 0
                  ? `${selectedAnimators.length} ${translate(
                      language,
                      `PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.PLACEHOLDERS.${
                        selectedAnimators.length === 1 ? "ANIMATOR_SELECTED" : "ANIMATORS_SELECTED"
                      }`
                    )}`
                  : translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.PLACEHOLDERS.ANIMATORS")
              }
              items={animators}
              onConfirm={setSelectedAnimators}
              badgeItemProperty="email"
              displayItem={(item) => item.email}
              language={language}
              showAlwaysConfirm
              toggleClass={`text-truncate w-100 border-secondary ${selectedAnimators.length === 0 ? "text-muted" : ""}`}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.SERVICE_LINK_GENERATION")}
            </Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex justify-content-between align-items-center border-secondary"
                variant=""
              >
                {meetingLinkService === "zoom" &&
                  translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM"
                  )}
                {meetingLinkService === "teams" &&
                  translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS"
                  )}
                {meetingLinkService === null &&
                  translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE"
                  )}
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100">
                <Dropdown.Item onClick={() => setMeetingLinkService("zoom")}>
                  {translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM"
                  )}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setMeetingLinkService("teams")}>
                  {translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS"
                  )}
                </Dropdown.Item>

                <Dropdown.Item onClick={() => setMeetingLinkService(null)}>
                  {translate(
                    language,
                    "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE"
                  )}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {meetingLinkService === null && (
            <Form.Group className="mt-3">
              <Form.Label>
                {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.MEETING_LINK")}
              </Form.Label>
              <Form.Control
                className={`border-secondary ${!meetingLink ? "text-muted" : ""}`}
                type="text"
                placeholder={translate(
                  language,
                  "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.PLACEHOLDERS.MEETING_LINK"
                )}
                value={meetingLink}
                onChange={(event) => setMeetingLink(event.target.value)}
              />
            </Form.Group>
          )}

          <Form.Group className="mt-3">
            <Form.Label>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.LABELS.DESCRIPTION")}
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!description ? "text-muted" : ""}`}
              as="textarea"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Form.Group>
        </Form>

        <Modal.Footer>
          <DstButton
            variant="secondary"
            value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.BUTTONS.CANCEL")}
            clickFunction={() => {
              setShowCreationModal(false);
              resetSelection();
            }}
          />
          <DstButton
            value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.CREATE_MODAL.BUTTONS.CONFIRM")}
            clickFunction={handleConfirm}
            disabled={!isFormValid}
            loading={loading.submit}
          />
        </Modal.Footer>
      </Popup>
      <DstAlert show={showAlert} onHide={_handleAlertClose} {...alertProps} />
    </React.Fragment>
  );
};

export default CreateMeetingModal;
